import classNames from 'classnames';
import React from 'react';
import Image from '@/components/atoms/image/image';
import RichText from '@/components/atoms/rich-text/rich-text';
import SkeletonLoader from '@/components/atoms/skeleton-loader/skeleton-loader';
import Typography from '@/components/atoms/typography/typography';
import Tag from '@/components/molecules/tag/tag';
import Link from '@/components/utilities/link/link';
import { useWindowSize } from '@/libraries/hooks/use-window-size';
import styles from './content-search-card.module.scss';

export interface ContentSearchResult {
  /** Description of the card */
  description?: string;
  /** Headline of the card */
  headline?: string;
  /** The image of the card */
  image?: {
    altText: string;
    url: string;
  };
  /** The tag of the card */
  tag?: string;
  /** The url to link to */
  url?: string;
}

export interface ContentSearchCardProps {
  /** The class name to apply */
  className?: string;
  /** Indicates whether to display the loading state */
  isLoading?: boolean;
  /** The item to show */
  item?: ContentSearchResult;
}

const ContentSearchCard = ({
  className,
  isLoading = false,
  item,
}: ContentSearchCardProps) => {
  const isLaptop = useWindowSize('laptop');

  if (isLoading) {
    return (
      <div
        className={classNames(styles.component, styles.isLoading, className)}
      >
        <div className={styles.wrapper}>
          <div className={styles.content}>
            <div className={styles.imageWrapper}>
              {isLaptop ? (
                <SkeletonLoader
                  className={styles.image}
                  height="114px"
                  width="204px"
                />
              ) : (
                <SkeletonLoader className={styles.image} />
              )}
            </div>

            {!isLaptop && (
              <SkeletonLoader
                borderRadius="4px"
                className={styles.tag}
                height="12px"
                width="44px"
              />
            )}
          </div>

          <div className={styles.details}>
            {isLaptop && (
              <SkeletonLoader
                borderRadius="4px"
                className={styles.tag}
                height="12px"
                width="44px"
              />
            )}

            <SkeletonLoader borderRadius="4px" height="16px" width="80px" />

            {isLaptop ? (
              <SkeletonLoader borderRadius="4px" height="70px" width="100%" />
            ) : (
              <SkeletonLoader borderRadius="4px" height="88px" width="100%" />
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!item) return null;

  const { description, headline, image, tag, url } = item;

  const renderTag = () => {
    if (!tag) return null;

    return <Tag className={styles.tag} text={tag} variant="secondary" />;
  };

  return (
    <Link
      className={classNames(styles.component, className, {
        [styles.hasNoImage]: !image,
      })}
      url={url}
    >
      <div className={styles.wrapper}>
        <div className={styles.content}>
          {image && (
            <div className={styles.imageWrapper}>
              <Image
                alt={image.altText}
                className={styles.image}
                height={88}
                src={image.url}
                srcSet={{
                  laptop: {
                    height: 114,
                    width: 204,
                  },
                }}
                unoptimized
                width={88}
              />
            </div>
          )}

          {!isLaptop && renderTag()}
        </div>

        <div className={styles.details}>
          {isLaptop && renderTag()}

          {headline && (
            <Typography className={styles.headline} tagStyle="headingSmall">
              {headline}
            </Typography>
          )}

          {description && (
            <RichText className={styles.description} text={description} />
          )}
        </div>
      </div>
    </Link>
  );
};

ContentSearchCard.displayName = 'ContentSearchCard';

export default ContentSearchCard;
